import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useRef, useState, useMemo, useEffect, createElement } from 'react';
import { isArray, isNullOrUndefined, __DEV__, isString, isEmptyObject } from '../../utils/assertion.esm.js';
import '../../utils/ssr.esm.js';
import { roundToStep, formatNumber, getDecimalPlaces, clamp, closest } from '../../utils/number.esm.js';
import '../../utils/array.esm.js';
import '../../utils/breakpoint.esm.js';
import '../../utils/context.esm.js';
import '../../utils/dom.esm.js';
import '../../utils/tabbable.esm.js';
import '../../utils/dom-query.esm.js';
import '../../utils/logger.esm.js';
import '../../utils/focus.esm.js';
import { noop } from '../../utils/function.esm.js';
import '../../utils/keydown.esm.js';
import '../../utils/lazy.esm.js';
import '../../utils/object.esm.js';
import '../../utils/react-helpers.esm.js';
import '../../utils/style-props.esm.js';
import '../../utils/tailwind.esm.js';
import Box from '../Box/index.esm.js';
import Text from '../Text/index.esm.js';
import { useRanger } from 'react-ranger';
import Fill from './Fill/index.esm.js';
import Handle from './Handle/index.esm.js';
import Input from './Input/index.esm.js';
import Track from './Track/index.esm.js';

function RangeInput(props) {
  var language = props.language,
      _props$format = props.format,
      format = _props$format === void 0 ? 'default' : _props$format,
      _props$currency = props.currency,
      currency = _props$currency === void 0 ? 'USD' : _props$currency,
      _props$fixedPoint = props.fixedPoint,
      fixedPoint = _props$fixedPoint === void 0 ? false : _props$fixedPoint,
      _props$onChange = props.onChange,
      onChange = _props$onChange === void 0 ? noop : _props$onChange,
      _props$onInput = props.onInput,
      onInput = _props$onInput === void 0 ? noop : _props$onInput,
      _props$min = props.min,
      min = _props$min === void 0 ? 0 : _props$min,
      _props$max = props.max,
      max = _props$max === void 0 ? 100 : _props$max,
      _props$value = props.value,
      valueProp = _props$value === void 0 ? [min, max] : _props$value,
      _props$step = props.step,
      step = _props$step === void 0 ? 1 : _props$step,
      steps = props.steps,
      _props$showInputs = props.showInputs,
      showInputs = _props$showInputs === void 0 ? false : _props$showInputs,
      tick = props.tick,
      ticksMapping = props.ticksMapping,
      rest = _objectWithoutPropertiesLoose(props, ["language", "format", "currency", "fixedPoint", "onChange", "onInput", "min", "max", "value", "step", "steps", "showInputs", "tick", "ticksMapping"]);

  var trackRef = useRef(null); // Format a value to be presented in the UI as a label

  var formatLabel = function formatLabel(input) {
    if (format === 'price' && typeof input === 'number') {
      var formatted = formatNumber(input, {
        style: 'currency',
        currency: currency
      });
      return fixedPoint ? formatted : formatted.replace('.00', '');
    }

    return fixedPoint ? Number(input).toFixed(getDecimalPlaces(step)) : input.toLocaleString(language);
  }; // Format a value for the input


  var formatInputValue = function formatInputValue(input) {
    return fixedPoint ? Number(input).toFixed(getDecimalPlaces(step)) : input;
  }; // Map RangeValue to the closest step and clamp values


  var mapRange = function mapRange(value) {
    return isArray(value) ? value.map(function (v) {
      return roundToStep(clamp(Number(v), min, max), step);
    }) : roundToStep(clamp(Number(value), min, max), step);
  }; // Map RangeValue to RangeInputValue


  var mapRangeInput = function mapRangeInput(value) {
    return (isArray(value) ? value : [value]).map(String).map(formatInputValue);
  };

  var _props$ticks = props.ticks,
      ticksProp = _props$ticks === void 0 ? !tick ? [min, max] : undefined : _props$ticks;

  var _React$useState = useState(mapRangeInput(valueProp)),
      inputValues = _React$useState[0],
      setInputValues = _React$useState[1];

  var _React$useState2 = useState(mapRange(valueProp)),
      range = _React$useState2[0],
      setRange = _React$useState2[1];

  var values = useMemo(function () {
    return mapRange(range);
  }, [range]);

  var _ref = isArray(values) ? values : [values],
      low = _ref[0],
      high = _ref[1]; // Map and set a range


  var mapSetRange = function mapSetRange(newValue, fireOnChange) {
    if (fireOnChange === void 0) {
      fireOnChange = false;
    }

    var newRange = mapRange(newValue);
    setRange(newRange);
    onInput(newRange);

    if (fireOnChange) {
      onChange(newRange);
    }
  }; // Set internal values when prop changes


  useEffect(function () {
    return setRange(mapRange(valueProp));
  }, [valueProp]); // Set input values when the range changes

  useEffect(function () {
    return setInputValues(mapRangeInput(range));
  }, [range]);

  var _useRanger = useRanger({
    stepSize: step,
    steps: steps,
    min: min,
    max: max,
    values: isArray(values) ? values : [values],
    tickSize: tick,
    ticks: ticksProp,
    onDrag: function onDrag(val) {
      return mapSetRange(val.length === 1 ? val[0] : val);
    },
    onChange: function onChange(val) {
      return mapSetRange(val.length === 1 ? val[0] : val, true);
    }
  }),
      getTrackProps = _useRanger.getTrackProps,
      handles = _useRanger.handles,
      ticks = _useRanger.ticks,
      segments = _useRanger.segments;

  var handleRangeInputChange = function handleRangeInputChange(left, v) {
    var val = isString(v) ? Number(v) : v;
    var newValue;

    if (isNullOrUndefined(high)) {
      newValue = [val < min ? min : val];
    } else if (left) {
      newValue = [val < min ? min : val, high];
    } else {
      newValue = [low, val > max ? max : val];
    }

    mapSetRange(newValue, true);
  };

  var handleSwitchRange = function handleSwitchRange() {
    if (isNullOrUndefined(high)) {
      return;
    }

    if (low > high) {
      mapSetRange([high, low], true);
    }
  };

  var handleSegmentClick = function handleSegmentClick(event, i) {
    if ((trackRef == null ? void 0 : trackRef.current) === null) {
      return;
    } // Calculate percentage


    var clientRect = trackRef.current.getBoundingClientRect();
    var percent = clamp(100 / clientRect.width * (event.clientX - clientRect.left), 0, 100);
    var newValue = roundToStep((max - min) * (percent / 100), step);

    if (i === 1 && !isNullOrUndefined(high)) {
      // Determine closest handle if clicking in center section
      var _closest = closest(newValue, [low, high]),
          index = _closest[0];

      handleRangeInputChange(index === 0, newValue);
    } else {
      handleRangeInputChange(i === 0, newValue);
    }
  };

  var handleInputChange = function handleInputChange(left) {
    return function (event) {
      var value = event.target.value;
      var l = inputValues[0],
          r = inputValues[1];

      if (left && !isNullOrUndefined(r)) {
        setInputValues([value, r]);
      } else if (!isNullOrUndefined(r)) {
        setInputValues([l, value]);
      } else {
        setInputValues([value]);
      }
    };
  };

  var handleInputKeyDown = function handleInputKeyDown(left) {
    return function (event) {
      var target = event.target;

      if (event.key === 'Enter') {
        handleRangeInputChange(left, target.value);
      }
    };
  };

  var inputProps = {
    min: min,
    max: max,
    step: step,
    type: 'number',
    inputMode: 'numeric',
    onBlur: handleSwitchRange,
    label: 'Range input',
    fontSize: 'text-sm'
  };

  var leftInputProps = _extends({}, inputProps, {
    value: inputValues[0],
    onChange: handleInputChange(true),
    onKeyDown: handleInputKeyDown(true),
    label: 'Range input left bound'
  });

  var rightInputProps = _extends({}, inputProps, {
    value: inputValues[1],
    onChange: handleInputChange(false),
    onKeyDown: handleInputKeyDown(false),
    label: 'Range input right bound'
  });

  var leftInput = createElement(Input, _extends({}, leftInputProps));
  var rightInput = !isNullOrUndefined(high) ? createElement(Input, _extends({}, rightInputProps)) : null;

  var _getTrackProps = getTrackProps({
    ref: trackRef
  }),
      trackStyle = _getTrackProps.style,
      trackProps = _objectWithoutPropertiesLoose(_getTrackProps, ["style"]);

  return createElement(Box, _extends({
    display: "flex",
    flexDirection: "flex-col",
    padding: "px-2"
  }, rest), createElement(Box, {
    width: "w-full",
    margin: ['mt-8', 'mb-3']
  }, createElement(Box, {
    position: "relative"
  }, ticks.map(function (_ref2) {
    var tickValue = _ref2.value,
        getTickProps = _ref2.getTickProps;

    // Remove width from the styles to prevent needing !important in our styles
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    var _getTickProps = getTickProps(),
        style = _getTickProps.style,
        tickProps = _objectWithoutPropertiesLoose(_getTickProps, ["style"]);

    var tickStyles = _objectWithoutPropertiesLoose(style, ["width"]);

    var hasMapping = !isEmptyObject(ticksMapping);

    if (hasMapping && isNullOrUndefined(ticksMapping == null ? void 0 : ticksMapping[tickValue])) {
      return null;
    }

    return createElement(Text, _extends({}, tickProps, {
      margin: "mb-2.5",
      fontSize: "text-xs",
      textAlign: "text-center",
      textColor: "text-gray-400",
      style: _extends({}, tickStyles),
      dangerouslySetClassName: "range-input__tick-item"
    }), formatLabel(hasMapping ? ticksMapping == null ? void 0 : ticksMapping[tickValue] : tickValue));
  })), createElement(Track, _extends({}, trackProps, {
    style: trackStyle
  }), segments.map(function (_ref3, i) {
    var getSegmentProps = _ref3.getSegmentProps;

    var _getSegmentProps = getSegmentProps({
      index: i,
      onClick: function onClick(e) {
        return handleSegmentClick(e, i);
      }
    }),
        style = _getSegmentProps.style,
        fillProps = _objectWithoutPropertiesLoose(_getSegmentProps, ["style"]);

    return createElement(Fill, _extends({
      isSingleHandle: isNullOrUndefined(high)
    }, fillProps, {
      style: style
    }));
  }), handles.map(function (_ref4) {
    var handleValue = _ref4.value,
        active = _ref4.active,
        getHandleProps = _ref4.getHandleProps;

    var _getHandleProps = getHandleProps({
      active: active
    }),
        style = _getHandleProps.style,
        handleProps = _objectWithoutPropertiesLoose(_getHandleProps, ["style"]);

    return createElement(Handle, _extends({
      "data-value": formatLabel(handleValue)
    }, handleProps, {
      style: style
    }));
  }))), showInputs && createElement(Box, {
    display: "flex",
    flexDirection: ['flex-col', 'sm:flex-row'],
    alignItems: "items-center",
    justifyContent: isNullOrUndefined(high) ? 'justify-end' : 'justify-between'
  }, leftInput, rightInput));
}

if (__DEV__) {
  RangeInput.displayName = 'RangeInput';
}

export default RangeInput;
//# sourceMappingURL=index.esm.js.map
